import React, { useState, useEffect } from "react";
import { X, Send, User } from "lucide-react";
import {
  collection,
  addDoc,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase";
import { useAppContext } from "../../context/AppContext";

export default function CommentsModal({
  commentPostId,
  setCommentPostId,
}: any) {
  const { t, user, activePet } = useAppContext();
  const [comments, setComments] = useState<any[]>([]);
  const [newComment, setNewComment] = useState("");

  useEffect(() => {
    if (!commentPostId) return;
    const q = query(
      collection(db, "posts", commentPostId, "comments"),
      orderBy("createdAt", "asc")
    );
    const unsub = onSnapshot(q, (snap) => {
      setComments(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [commentPostId]);

  const handleSend = async () => {
    if (!newComment.trim() || !user) return;
    const text = newComment;
    setNewComment("");
    await addDoc(collection(db, "posts", commentPostId, "comments"), {
      text,
      authorId: user.uid,
      authorName: activePet?.name || "User",
      authorPhoto: activePet?.photo || null,
      time: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }),
      createdAt: serverTimestamp(),
    });
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[600] flex justify-center items-end sm:items-center animate-fade-in">
      <div className="bg-[var(--bg-main)] w-full sm:w-[400px] h-[80vh] sm:h-[600px] rounded-t-3xl sm:rounded-3xl flex flex-col relative overflow-hidden animate-slide-up sm:animate-zoom-in shadow-2xl">
        {/* Header */}
        <div className="bg-[var(--bg-card)] border-b border-[var(--border-color)] px-5 py-4 flex justify-between items-center shrink-0">
          <h3 className="font-extrabold text-lg text-[var(--text-main)]">
            Коментарі
          </h3>
          <button
            onClick={() => setCommentPostId(null)}
            className="p-2 bg-[var(--bg-soft)] rounded-full text-[var(--text-muted)] hover:text-[var(--text-main)] transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Comments List */}
        <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-4">
          {comments.length === 0 ? (
            <div className="text-center py-10 text-[var(--text-muted)] text-sm">
              Поки що немає коментарів.
            </div>
          ) : (
            comments.map((comment) => (
              <div key={comment.id} className="flex gap-3 items-start">
                <div className="w-10 h-10 rounded-full bg-[var(--accent-soft)] overflow-hidden shrink-0 flex items-center justify-center border border-[var(--border-color)]">
                  {comment.authorPhoto ? (
                    <img
                      src={comment.authorPhoto}
                      className="w-full h-full object-cover"
                      alt="avatar"
                    />
                  ) : (
                    <User size={20} className="text-[#a073e7]" />
                  )}
                </div>

                {/* МАГІЯ ТУТ: min-w-0 */}
                <div className="flex-1 min-w-0 bg-[var(--bg-card)] p-3 rounded-2xl rounded-tl-none border border-[var(--border-color)] shadow-sm">
                  <div className="flex justify-between items-center mb-1 gap-2">
                    {/* МАГІЯ ТУТ: truncate для імені */}
                    <h4 className="font-bold text-sm text-[var(--text-main)] truncate max-w-[150px]">
                      {comment.authorName}
                    </h4>
                    <span className="text-[10px] text-[var(--text-muted)] shrink-0">
                      {comment.time}
                    </span>
                  </div>
                  {/* МАГІЯ ТУТ: break-words для спаму */}
                  <p className="text-sm text-[var(--text-main)] break-words whitespace-pre-wrap">
                    {comment.text}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Input Area */}
        <div className="p-4 bg-[var(--bg-card)] border-t border-[var(--border-color)] shrink-0">
          <div className="flex items-center gap-2 bg-[var(--bg-soft)] border border-[var(--border-color)] rounded-full px-2 py-1.5">
            <input
              type="text"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSend()}
              placeholder="Написати коментар..."
              className="flex-1 bg-transparent px-3 outline-none text-sm text-[var(--text-main)]"
            />
            <button
              onClick={handleSend}
              disabled={!newComment.trim()}
              className="p-2.5 bg-[#a073e7] rounded-full text-white disabled:opacity-50 transition-all shadow-md"
            >
              <Send size={18} className="ml-0.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
